import { buildTokenPattern } from "./mailTemplateText";
import {
  SCHEDULE_MAIL_PARAMETERS,
  type ScheduleMailUseCase,
} from "./scheduleMailTemplate";

/**
 * 対象者ごとに決まる値のサンプル。設定画面では実値を出せないのでこれを差し込む。
 *
 * 氏名は「課地世 花子＝クライアント」「部地世 太郎＝クライアントについて語る回答者」で固定する。
 * ステークホルダーインタビューは「部地世 太郎さんに、課地世 花子さんについて聞く」関係になるので、
 * 同じ @system_interviewee_name でもユースケースで指す人が変わる。
 */
export const PREVIEW_SAMPLES = {
  clientName: "課地世 花子",
  stakeholderRespondentName: "部地世 太郎",
  reservationUrl:
    "https://interview.mento.jp/employee_interview/schedule/token_abc",
  intervieweeType: "管理職",
} as const;

/** プレビューの1片。`actual: false` の片は網掛けにして、送信時に置き換わることを示す */
export type PreviewSegment =
  | { type: "text"; text: string }
  | { type: "value"; name: string; text: string; actual: boolean };

export type PreviewValue = { text: string; actual: boolean };

export type BuildPreviewValuesInput = {
  useCase: ScheduleMailUseCase;
  companyName: string | null;
  interviewTitle: string | null;
  durationMinutes: number;
  startDate?: string;
  endDate?: string;
  deadlineDate?: string;
};

/**
 * "YYYY-MM-DD" を「2026年7月22日」にする。ゼロ埋めしないのは backend の
 * `format_japanese_date` に合わせるため。
 *
 * `new Date()` は使わない。"2026-07-22" は UTC の深夜としてパースされるので、
 * 負のオフセットのタイムゾーンでは1日ずれる。
 */
function formatJapaneseDate(value: string | undefined): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const [year, month, day] = value.split("-");
  return `${year}年${Number(month)}月${Number(day)}日`;
}

function formatMinutes(value: number): string {
  return Number.isFinite(value) ? String(Math.trunc(value)) : "";
}

/** 送信時に置き換わる値を、トークン名で引ける形に組み立てる */
export function buildScheduleMailPreviewValues(
  input: BuildPreviewValuesInput,
): Record<string, PreviewValue> {
  const isStakeholder = input.useCase === "STAKEHOLDER";
  const sample = (text: string): PreviewValue => ({ text, actual: false });
  // 会社名とインタビュータイトルは、この画面から辿れる実値。取れないときにサンプルで埋めると
  // 「設定済みに見える」ので、空にして未設定の注意に載せる（日付や所要時間と同じ扱い）
  const fromProp = (value: string | null): PreviewValue => ({
    text: value ?? "",
    actual: true,
  });

  return {
    system_company_name: fromProp(input.companyName),
    system_interview_title: fromProp(input.interviewTitle),
    system_interviewee_name: sample(
      isStakeholder
        ? PREVIEW_SAMPLES.stakeholderRespondentName
        : PREVIEW_SAMPLES.clientName,
    ),
    // ステークホルダー以外では backend が空リストを空文字に落とす。実値扱いにして網掛けしない
    system_recipient_names: isStakeholder
      ? sample(`・${PREVIEW_SAMPLES.clientName} さま`)
      : { text: "", actual: true },
    system_reservation_url: sample(PREVIEW_SAMPLES.reservationUrl),
    system_interviewee_type: sample(PREVIEW_SAMPLES.intervieweeType),
    interview_time: {
      text: formatMinutes(input.durationMinutes),
      actual: true,
    },
    start_date: { text: formatJapaneseDate(input.startDate), actual: true },
    end_date: { text: formatJapaneseDate(input.endDate), actual: true },
    reservation_deadline: {
      text: formatJapaneseDate(input.deadlineDate),
      actual: true,
    },
  };
}

/**
 * 文面のトークンを値に置き換えて、網掛けの有無が分かる形に切り分ける。
 *
 * 照合には全10トークンを使う。backend の `_TOKEN_PATTERN` はユースケースに関係なく
 * 固定の10トークンなので、ユースケースで絞ると「コーチングの本文に
 * @system_recipient_names を手で書いた」ときに出力が食い違う。
 */
export function renderMailPreviewSegments(
  text: string,
  values: Record<string, PreviewValue>,
): PreviewSegment[] {
  const pattern = buildTokenPattern(SCHEDULE_MAIL_PARAMETERS);
  const segments: PreviewSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    const value = values[match[1]];
    // 値の定義が無いトークンは未知の @文字列と同じ扱いで温存する
    if (!value) continue;
    if (index > lastIndex) {
      segments.push({ type: "text", text: text.slice(lastIndex, index) });
    }
    segments.push({
      type: "value",
      name: match[1],
      text: value.text,
      actual: value.actual,
    });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", text: text.slice(lastIndex) });
  }
  return segments;
}

/** 置き換え後の文字列。backend の `render` と同じ出力になることをテストで固定する */
export function renderScheduleMailPreviewText(
  text: string,
  values: Record<string, PreviewValue>,
): string {
  return renderMailPreviewSegments(text, values)
    .map((segment) => segment.text)
    .join("");
}

/** 本文で使われていて、値が空になったトークン名 */
export function collectEmptyValueTokens(segments: PreviewSegment[]): string[] {
  const names = segments
    .filter(
      (segment): segment is Extract<PreviewSegment, { type: "value" }> =>
        segment.type === "value" && segment.text === "",
    )
    .map((segment) => segment.name);
  return [...new Set(names)];
}

/** 文面で使っているのに値が空だと、保存できないトークン。backend の `_CONFIG_VALUE_TOKEN_FIELDS` と対応する */
export const DATE_CONFIG_VALUE_TOKENS = [
  "start_date",
  "end_date",
  "reservation_deadline",
] as const;

export type DateConfigValueToken = (typeof DATE_CONFIG_VALUE_TOKENS)[number];

/** 文面で使っていて、値が空のため保存できないトークン名 */
export function findMissingConfigValueTokens(
  text: string,
  values: Record<string, PreviewValue>,
): DateConfigValueToken[] {
  const emptyTokens = collectEmptyValueTokens(
    renderMailPreviewSegments(text, values),
  );
  return DATE_CONFIG_VALUE_TOKENS.filter((name) => emptyTokens.includes(name));
}
